/** The authenticated frame: shell with nav links, user menu, and page outlet. */

import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getChangeSets } from "@pbxmgr/api";
import { AppShell, Button } from "@pbxmgr/ui";

import { useAuth } from "./auth/AuthProvider";
import { usePermission } from "./auth/usePermission";
import { useBranding } from "./branding";
import { SessionExpiryBanner } from "./components/SessionExpiryBanner";
import { WindowBanner } from "./components/WindowBanner";

export function AppChrome(): ReactNode {
  const { session, logout } = useAuth();
  const branding = useBranding();
  // Any one of the config app's gates is enough to make the link useful.
  // Each hook is called unconditionally (no ||-short-circuiting) so the
  // hook order is stable across renders.
  const canConfigureInstance = usePermission("instance.configure");
  const canManageRoles = usePermission("roles.manage");
  const canEditSystem = usePermission("system.settings");
  const canManageUsers = usePermission("users.manage");
  const canConfigure =
    canConfigureInstance || canManageRoles || canEditSystem || canManageUsers;
  // The scanner page (ADR 0049) is only for those who may swap phones.
  const canSwap = usePermission("devices.swap");
  // Pending staged routing changes surface in the nav (ADR 0032
  // notification requirement) so an approval/window never sits unseen.
  const changeSets = useQuery({
    queryKey: ["changesets"],
    queryFn: getChangeSets,
    refetchInterval: 60_000,
  });
  const pendingChanges = changeSets.data?.pending ?? 0;

  const nav = (
    <>
      <NavLink to="/" end>
        Dashboard
      </NavLink>
      <NavLink to="/cdr">Reporting</NavLink>
      <NavLink to="/pairing">Pairs</NavLink>
      <NavLink to="/dids">DIDs</NavLink>
      <NavLink to="/devices">Devices</NavLink>
      {canSwap ? <NavLink to="/swap">Swap</NavLink> : null}
      <NavLink to="/buttons">Buttons</NavLink>
      <NavLink to="/e911">E911</NavLink>
      <NavLink to="/activity">Activity</NavLink>
      <NavLink to="/changes">
        Changes{pendingChanges > 0 ? ` (${pendingChanges})` : ""}
      </NavLink>
      <NavLink to="/migration">Migration</NavLink>
      {/* The instance-configuration app is a separate SPA (brief §4A), so
          this is a plain link, not a router NavLink. Shown only to users
          who hold one of its permissions — otherwise it leads to a wall. */}
      {canConfigure ? <a href="/config/">Configuration</a> : null}
    </>
  );

  const userSlot = (
    <>
      <span>{session?.display_name}</span>
      <Button variant="secondary" onClick={() => void logout()}>
        Sign out
      </Button>
    </>
  );

  return (
    <>
      <SessionExpiryBanner />
      <WindowBanner />
      <AppShell
        productName={branding.product_name}
        logoSrc={branding.logo_path}
        nav={nav}
        userSlot={userSlot}
      >
        <Outlet />
      </AppShell>
    </>
  );
}
